/**
 * Haptic Feedback Utilities
 * 
 * Provides vibration/haptic feedback that works across:
 * - Web browsers (Vibration API)
 * - PWA (Vibration API)
 * - Native apps via Capacitor (Haptics plugin)
 */

import { Haptics, ImpactStyle } from '@capacitor/haptics';

/**
 * Check if haptics are available
 */
export const isHapticsAvailable = (): boolean => {
  // Check for Capacitor Haptics (native app)
  if (typeof Haptics !== 'undefined') {
    return true;
  }
  
  // Check for Web Vibration API (browser/PWA)
  return 'vibrate' in navigator;
};

/**
 * Trigger light haptic feedback (for subtle interactions)
 */
export const hapticLight = async (): Promise<void> => {
  try {
    // Try Capacitor Haptics first (native app)
    if (typeof Haptics !== 'undefined') {
      await Haptics.impact({ style: ImpactStyle.Light });
      return;
    }
    
    // Fallback to Vibration API (browser/PWA)
    if ('vibrate' in navigator) {
      navigator.vibrate(10); // 10ms light vibration
    }
  } catch (error) {
    // Silently fail - haptics are optional
    console.debug('Haptic feedback not available:', error);
  }
};

/**
 * Trigger medium haptic feedback (for standard interactions)
 */
export const hapticMedium = async (): Promise<void> => {
  try {
    // Try Capacitor Haptics first (native app)
    if (typeof Haptics !== 'undefined') {
      await Haptics.impact({ style: ImpactStyle.Medium });
      return;
    }
    
    // Fallback to Vibration API (browser/PWA)
    if ('vibrate' in navigator) {
      navigator.vibrate(20); // 20ms medium vibration
    }
  } catch (error) {
    console.debug('Haptic feedback not available:', error);
  }
};

/**
 * Trigger heavy haptic feedback (for important interactions)
 */
export const hapticHeavy = async (): Promise<void> => {
  try {
    // Try Capacitor Haptics first (native app)
    if (typeof Haptics !== 'undefined') {
      await Haptics.impact({ style: ImpactStyle.Heavy });
      return;
    }
    
    // Fallback to Vibration API (browser/PWA)
    if ('vibrate' in navigator) {
      navigator.vibrate(30); // 30ms heavy vibration
    }
  } catch (error) {
    console.debug('Haptic feedback not available:', error);
  }
};

/**
 * Trigger selection haptic feedback (for toggles, selections)
 */
export const hapticSelection = async (): Promise<void> => {
  try {
    // Try Capacitor Haptics first (native app)
    if (typeof Haptics !== 'undefined') {
      await Haptics.selectionStart();
      await Haptics.selectionChanged();
      await Haptics.selectionEnd();
      return;
    }
    
    // Fallback to Vibration API (browser/PWA) - subtle pattern
    if ('vibrate' in navigator) {
      navigator.vibrate([5, 10, 5]); // Short-pause-short pattern
    }
  } catch (error) {
    console.debug('Haptic feedback not available:', error);
  }
};

/**
 * Trigger notification haptic feedback (for alerts, confirmations)
 */
export const hapticNotification = async (type: 'success' | 'warning' | 'error' = 'success'): Promise<void> => {
  try {
    // Try Capacitor Haptics first (native app)
    if (typeof Haptics !== 'undefined') {
      // Capacitor doesn't have notification types, use impact instead
      const style = type === 'error' ? ImpactStyle.Heavy : 
                    type === 'warning' ? ImpactStyle.Medium : 
                    ImpactStyle.Light;
      await Haptics.impact({ style });
      return;
    }
    
    // Fallback to Vibration API (browser/PWA) - different patterns
    if ('vibrate' in navigator) {
      switch (type) {
        case 'success':
          navigator.vibrate([10, 50, 10]); // Double tap
          break;
        case 'warning':
          navigator.vibrate([20, 50, 20]); // Stronger double tap
          break;
        case 'error':
          navigator.vibrate([30, 50, 30, 50, 30]); // Triple tap
          break;
      }
    }
  } catch (error) {
    console.debug('Haptic feedback not available:', error);
  }
};

/**
 * Custom vibration pattern
 */
export const hapticCustom = async (pattern: number | number[]): Promise<void> => {
  try {
    // Capacitor doesn't support custom patterns easily, use medium impact
    if (typeof Haptics !== 'undefined') {
      await Haptics.impact({ style: ImpactStyle.Medium });
      return;
    }
    
    // Fallback to Vibration API (browser/PWA)
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  } catch (error) {
    console.debug('Haptic feedback not available:', error);
  }
};
